import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { DropDownMenuOption, MultiSelectDropDownOption } from '@replai-platform/ui-components';
import { RootState } from '../../../store/rootReducer';
import useGetAdSets from '../../../api/hooks/adSets/useGetAdSets';
import { Page } from '../../../utils/enums';
import { FilterActions } from '../../../store/filters';
import { api } from '../../../api';

const HALF_DAY_IN_MS = 12 * 60 * 60 * 1000;

const useAdSetFilter = ({
  initialOptions,
  page,
}: {
  initialOptions?: string[];
  page?: Page;
}): {
  adSetFilterOptions: DropDownMenuOption[];
  isLoading: boolean;
  hasData: boolean;
  adSetFilterOnChange: (options: MultiSelectDropDownOption[]) => void;
} => {
  const projectId = useSelector((state: RootState) => state.project.id);
  const filtering = useSelector((state: RootState) => state.filters);
  const dispatch = useDispatch();
  const isPageVideosOrTags = page === Page.Videos || page === Page.Tags;

  const getDateTimestamps = useCallback(() => {
    const { dateStartTimestamp, dateEndTimestamp } = api.filterConverter.getMetricsFilters(filtering);
    return {
      dateStartTimestamp,
      dateEndTimestamp,
    };
  }, [filtering]);

  // Fetch and update project networks
  const { isSuccess, data, isLoading } = useGetAdSets(
    {
      projectIds: [projectId],
      metricsFilters: isPageVideosOrTags ? getDateTimestamps() : undefined,
    },
    {
      onSuccess: (res) => {
        const adSetIds = res.adSets.map(({ id }) => id);
        dispatch(FilterActions.changeAdSetsIds({ value: adSetIds, page, logEvent: false }));
      },
      cacheTime: HALF_DAY_IN_MS,
    }
  );

  const mappedAdSets = useMemo(
    () => data?.adSets?.map(({ id, name }) => ({ adSetId: id, adSetName: name })) ?? [],
    [data]
  );

  const adSetFilterOptions = useMemo(() => {
    if (!isSuccess || !data) return [];
    return [
      ...(!isPageVideosOrTags
        ? [
            {
              label: 'All Ad Sets',
              isAllOption: true,
              selected: !initialOptions,
            },
          ]
        : []),
      ...(mappedAdSets ?? []).map(({ adSetId, adSetName }) => ({
        id: adSetId,
        label: `${adSetName} (${adSetId})`,
        selected: initialOptions ? initialOptions.includes(adSetId) : !isPageVideosOrTags,
      })),
    ];
  }, [data, isSuccess, initialOptions, isPageVideosOrTags, mappedAdSets]);

  const adSetFilterOnChange = useCallback(
    (selectedOptions: MultiSelectDropDownOption[]) => {
      const selectedAdSetsIds = selectedOptions
        .filter(({ selected, id }) => selected && id !== undefined)
        .map(({ id }) => id) as string[];
      dispatch(
        FilterActions.changeAdSetsIds({
          value: selectedAdSetsIds.length !== (data?.adSets?.length ?? 0) ? selectedAdSetsIds : [],
          page,
          logEvent: true,
        })
      );
    },
    [dispatch, page, data?.adSets?.length]
  );

  return { adSetFilterOptions, isLoading, hasData: adSetFilterOptions.length > 0, adSetFilterOnChange };
};

export default useAdSetFilter;
